import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, File, Image, Video, Music, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useMedia } from '@/contexts/MediaContext';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

const MediaUpload = ({ onUploadComplete }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState({}); // 'pending', 'success', 'error'
  const fileInputRef = useRef(null);
  const { uploadFile } = useMedia();
  const { user } = useAuth();
  const { toast } = useToast();

  const acceptedTypes = {
    'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff', '.svg'],
    'video/*': ['.mp4', '.avi', '.mov', '.mkv', '.webm', '.flv', '.wmv', '.3gp'],
    'audio/*': ['.mp3', '.wav', '.ogg', '.aac', '.flac', '.m4a', '.wma'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx']
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return <Image className="h-5 w-5 text-cyan-400" />;
    if (file.type.startsWith('video/')) return <Video className="h-5 w-5 text-blue-400" />;
    if (file.type.startsWith('audio/')) return <Music className="h-5 w-5 text-purple-400" />;
    return <File className="h-5 w-5 text-slate-400" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const validFiles = files.filter(file => {
      const isValidType = Object.keys(acceptedTypes).some(type => {
        if (type === 'image/*') return file.type.startsWith('image/');
        if (type === 'video/*') return file.type.startsWith('video/');
        if (type === 'audio/*') return file.type.startsWith('audio/');
        return file.type === type;
      });
      
      if (!isValidType) {
        toast({
          title: "Arquivo não suportado",
          description: `${file.name} não é um tipo de arquivo suportado.`,
          variant: "destructive",
        });
        return false;
      }
      
      // Verificar tamanho (2GB max)
      if (file.size > 2147483648) {
        toast({
          title: "Arquivo muito grande",
          description: `${file.name} excede o limite de 2GB.`,
          variant: "destructive",
        });
        return false;
      }
      
      return true;
    });
    
    // Add unique ID to files to track progress correctly even with duplicate names
    const newFiles = validFiles.map(file => {
        file.uid = Math.random().toString(36).substring(2) + Date.now().toString(36);
        return file;
    });

    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (uid) => {
    if (uploading) return;
    setSelectedFiles(prev => prev.filter(file => file.uid !== uid));
    setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[uid];
        return newProgress;
    });
    setUploadStatus(prev => {
        const newStatus = { ...prev };
        delete newStatus[uid];
        return newStatus;
    });
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    if (!user) {
      toast({
        title: "Erro",
        description: "Você precisa estar logado para fazer upload.",
        variant: "destructive",
      });
      return;
    }
    
    setUploading(true);
    let successCount = 0;
    let failCount = 0;

    // Upload sequentially to avoid overwhelming the network/browser
    // Or parallel with limit. For simplicity and progress tracking, let's do parallel
    
    const uploadPromises = selectedFiles.map(async (file) => {
      if (uploadStatus[file.uid] === 'success') return; // Skip already uploaded

      setUploadStatus(prev => ({ ...prev, [file.uid]: 'uploading' }));
      
      try {
        const result = await uploadFile(file, user.uid, (progress) => {
            setUploadProgress(prev => ({ ...prev, [file.uid]: progress }));
        });

        if (result.success) {
          successCount++;
          setUploadStatus(prev => ({ ...prev, [file.uid]: 'success' }));
          setUploadProgress(prev => ({ ...prev, [file.uid]: 100 }));
        } else {
          failCount++;
          setUploadStatus(prev => ({ ...prev, [file.uid]: 'error' }));
        }
      } catch (error) {
        console.error("Upload error for file", file.name, error);
        failCount++;
        setUploadStatus(prev => ({ ...prev, [file.uid]: 'error' }));
      }
    });

    await Promise.all(uploadPromises);

    setUploading(false);

    if (failCount === 0) {
      toast({
        title: "Upload concluído!",
        description: `${successCount} arquivo(s) enviado(s) com sucesso.`,
      });
      // Clear files after a short delay
      setTimeout(() => {
          setSelectedFiles([]);
          setUploadProgress({});
          setUploadStatus({});
          if (onUploadComplete) onUploadComplete();
      }, 1500);
    } else {
      toast({
        title: "Upload concluído com erros",
        description: `${successCount} enviado(s), ${failCount} falha(s).`,
        variant: "destructive",
      });
      // Don't clear files so user can retry or see errors
      if (onUploadComplete) onUploadComplete(); // Refresh list anyway
    }
  };

  return (
    <div className="space-y-6">
      {/* Drop Area */}
      {!uploading && (
        <div
          className={`relative overflow-hidden border-2 border-dashed rounded-2xl p-8 sm:p-12 text-center transition-all duration-300 group cursor-pointer ${
            isDragOver
              ? 'border-cyan-500 bg-cyan-500/10 scale-[1.01]'
              : 'border-slate-700 hover:border-cyan-500/50 hover:bg-slate-800/50'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          
          <div className="relative z-10 flex flex-col items-center gap-4">
            <div className={`p-4 rounded-full transition-all duration-300 ${isDragOver ? 'bg-cyan-500/20 text-cyan-400' : 'bg-slate-800 text-slate-400 group-hover:text-cyan-400 group-hover:scale-110'}`}>
              <Upload className="h-8 w-8" />
            </div>
            
            <div>
              <h3 className="text-xl font-bold text-slate-200 mb-2 group-hover:text-white transition-colors">
                {isDragOver ? 'Solte os arquivos aqui' : 'Arraste arquivos ou clique'}
              </h3>
              <p className="text-slate-500 text-sm max-w-sm mx-auto group-hover:text-slate-400 transition-colors">
                Suporte para imagens, vídeos, áudios e documentos até 2GB
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-2 mt-2">
              {['JPG', 'PNG', 'MP4', 'MP3', 'PDF', 'DOCX', 'XLSX', 'PPTX'].map((ext) => (
                <span key={ext} className="px-2 py-1 rounded-md bg-slate-800/50 border border-slate-700 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  {ext}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* File List */}
      <AnimatePresence>
        {selectedFiles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between text-sm text-slate-400 px-1">
              <span>{selectedFiles.length} arquivo(s) selecionado(s)</span>
              <button 
                onClick={() => {
                    setSelectedFiles([]);
                    setUploadProgress({});
                    setUploadStatus({});
                }}
                disabled={uploading}
                className="text-slate-500 hover:text-red-400 transition-colors disabled:opacity-50"
              >
                Limpar tudo
              </button>
            </div>

            <div className="space-y-2 max-h-[300px] overflow-y-auto custom-scrollbar pr-2">
              {selectedFiles.map((file) => (
                <motion.div
                  key={file.uid}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-3 flex items-center gap-3 group hover:border-slate-600 transition-colors"
                >
                  <div className="p-2 bg-slate-900/50 rounded-lg">
                    {getFileIcon(file)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-slate-200 truncate pr-2">{file.name}</p>
                      <span className="text-xs text-slate-500 whitespace-nowrap">{formatFileSize(file.size)}</span>
                    </div>
                    
                    {/* Progress Bar or Status */}
                    {uploadStatus[file.uid] === 'uploading' && (
                        <div className="w-full h-1.5 bg-slate-700 rounded-full overflow-hidden">
                            <motion.div 
                                className="h-full bg-cyan-500"
                                initial={{ width: 0 }}
                                animate={{ width: `${uploadProgress[file.uid] || 0}%` }}
                                transition={{ duration: 0.2 }}
                            />
                        </div>
                    )}
                    {uploadStatus[file.uid] === 'success' && (
                        <div className="flex items-center gap-1 text-green-400 text-xs">
                            <CheckCircle className="w-3 h-3" /> Concluído
                        </div>
                    )}
                    {uploadStatus[file.uid] === 'error' && (
                        <div className="flex items-center gap-1 text-red-400 text-xs">
                            <AlertCircle className="w-3 h-3" /> Erro
                        </div>
                    )}
                  </div>

                  {!uploading && uploadStatus[file.uid] !== 'success' && (
                    <button
                      onClick={() => removeFile(file.uid)}
                      className="p-1 text-slate-500 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </motion.div>
              ))}
            </div>

            <Button
              onClick={handleUpload}
              disabled={uploading}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white py-6 text-lg font-semibold rounded-xl shadow-lg shadow-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-5 w-5" />
                  Iniciar Upload
                </>
              )}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MediaUpload;
